import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Video, Globe, Wifi, Users, ExternalLink } from 'lucide-react';

interface ConnectionMethodSelectorProps {
  onSelectMethod: (method: 'webrtc' | 'jitsi' | 'google-meet') => void;
  bookingId: string;
  isVisible: boolean;
  userRole?: string;
}

const ConnectionMethodSelector = ({ onSelectMethod, bookingId, isVisible, userRole }: ConnectionMethodSelectorProps) => {
  const [selectedMethod, setSelectedMethod] = useState<'webrtc' | 'jitsi' | 'google-meet' | null>(null);
  const isAdmin = userRole === 'admin';

  if (!isVisible) return null;

  const handleMethodSelect = (method: 'webrtc' | 'jitsi' | 'google-meet') => {
    setSelectedMethod(method);
    onSelectMethod(method);
  };

  const jitsiRoomUrl = `https://meet.jit.si/tutornesta-${bookingId}`;
  const googleMeetUrl = `https://meet.google.com/new`;

  return (
    <div className="space-y-4">
      <div className="text-center mb-4">
        <h3 className="text-lg font-semibold mb-2">Ready to connect</h3>
        <p className="text-muted-foreground text-sm">Your learning session awaits</p>
        {userRole === 'tutor' && (
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg mt-3">
            <p className="text-blue-800 font-medium text-xs">
              💡 Log in with your personal email for the best experience
            </p>
          </div>
        )}
      </div>

      <div className="max-w-xs mx-auto">
        {/* Tuternesta Session (Jitsi Meet) */}
        <Card className="cursor-pointer hover:shadow-md transition-shadow border-2 border-primary">
          <CardHeader className="text-center pb-2">
            <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-2">
              <Video className="h-4 w-4 text-primary" />
            </div>
            <CardTitle className="text-sm">Tuternesta Session</CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-2 p-3">
            <div className="p-2 bg-green-50 border border-green-200 rounded">
              <p className="text-green-700 font-medium text-xs">
                📱 Use app or browser
              </p>
            </div>
            <div className="space-y-1 text-xs text-muted-foreground">
              <div>✓ No account required</div>
              <div>✓ Screen sharing & chat</div>
              <div>✓ Works on all devices</div>
            </div>
            <Button 
              onClick={() => handleMethodSelect('jitsi')}
              className="w-full"
              size="sm"
            >
              Start lesson
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Direct Connection - Commented out for future use */}
      {/* 
      <Card className="cursor-pointer hover:shadow-md transition-shadow">
        <CardHeader className="text-center pb-3 md:pb-4">
          <div className="w-10 h-10 md:w-12 md:h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2">
            <Wifi className="h-5 w-5 md:h-6 md:w-6 text-green-600" />
          </div>
          <CardTitle className="text-base md:text-lg">
            <span>Direct Connection</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="text-center space-y-2 md:space-y-3 p-3 md:p-6">
          <p className="text-xs md:text-sm text-muted-foreground">
            Connect directly through our platform with high-quality video and audio
          </p>
          <div className="space-y-1 text-xs text-muted-foreground">
            <div className="flex items-center justify-center gap-1">
              <Video className="h-3 w-3" />
              <span>HD Video & Audio</span>
            </div>
            <div className="flex items-center justify-center gap-1">
              <Users className="h-3 w-3" />
              <span>Built-in Chat & File Sharing</span>
            </div>
          </div>
          <Button 
            onClick={() => handleMethodSelect('webrtc')}
            variant="outline"
            className="w-full"
            size="sm"
          >
            Use Direct Connection
          </Button>
        </CardContent>
      </Card>
      */}

      {/* Google Meet Option - Commented out for future use */}
      {/* 
      <Card className={`cursor-pointer hover:shadow-md transition-shadow ${!isAdmin ? 'opacity-50' : ''}`}>
        <CardHeader className="text-center pb-3 md:pb-4">
          <div className="w-10 h-10 md:w-12 md:h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-2">
            <Globe className="h-5 w-5 md:h-6 md:w-6 text-blue-600" />
          </div>
          <CardTitle className="text-base md:text-lg flex flex-col md:flex-row items-center justify-center gap-1 md:gap-2">
            <span>Google Meet</span>
            {!isAdmin && <Badge variant="secondary" className="text-xs bg-yellow-100 text-yellow-800">Coming Soon</Badge>}
          </CardTitle>
        </CardHeader>
        <CardContent className="text-center space-y-2 md:space-y-3 p-3 md:p-6">
          <p className="text-xs md:text-sm text-muted-foreground">
            Use Google Meet for reliable video calling
          </p>
          <div className="space-y-1 text-xs text-muted-foreground">
            <div>✓ Reliable & stable</div>
            <div>✓ Recording available</div>
            <div>✓ Mobile apps</div>
          </div>
          <Button 
            onClick={() => isAdmin && handleMethodSelect('google-meet')}
            variant="outline"
            className="w-full"
            size="sm"
            disabled={!isAdmin}
          >
            {isAdmin ? 'Use Google Meet' : 'Coming Soon'}
          </Button>
        </CardContent>
      </Card>
      */}

      {selectedMethod === 'jitsi' && (
        <Card className="bg-green-50 border-green-200 mt-3">
          <CardContent className="pt-3 p-3">
            <div className="text-center">
              <p className="text-xs text-green-800 mb-2">
                Session ready!
              </p>
              <Button 
                onClick={() => window.open(jitsiRoomUrl, '_blank')}
                size="sm"
                className="bg-green-600 hover:bg-green-700 w-full"
              >
                <ExternalLink className="w-3 h-3 mr-1" />
                Join Now
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {selectedMethod === 'google-meet' && (
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="pt-4 p-3 md:p-6">
            <div className="text-center space-y-3">
              <p className="text-sm text-blue-800">
                Create a Google Meet room and share the link:
              </p>
              <Button 
                onClick={() => window.open(googleMeetUrl, '_blank')}
                size="sm"
                className="bg-blue-600 hover:bg-blue-700"
              >
                <ExternalLink className="w-3 h-3 md:w-4 md:h-4 mr-2" />
                Create Google Meet
              </Button>
              <p className="text-xs text-blue-600">
                Copy the meeting link and share it with the other participant
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ConnectionMethodSelector;